import { useEffect, useState } from 'react'

import {
  useCreateLeaveMutation,
  useCreateCalendarMeetingMutation,
  useDisconnectMicrosoftCalendarMutation,
  useUpdateCalendarEventMutation,
  useDisconnectGoogleCalendarMutation,
  useGetGoogleCalendarConnectUrlMutation,
  useGetGoogleCalendarStatusQuery,
  useGetMicrosoftCalendarConnectUrlMutation,
  useGetMicrosoftCalendarStatusQuery,
  useGetTeamOptionsQuery,
} from '../../../../api/serverStateHooks'
import { appToast } from '../../../../components/AppToast'
import AppDateInput from '../../../../components/AppDateInput'
import AppTimeInput from '../../../../components/AppTimeInput'
import AppDropdown from '../../../../components/AppDropdown'
import SearchableCombobox from '../../../../components/SearchableCombobox'

const ACTION_TYPES = [
  { id: 'meeting', label: 'Schedule Meeting', icon: 'calendar_today' },
  { id: 'leave', label: 'Mark on Leave', icon: 'event_busy' },
]

const MEETING_PROVIDER_OPTIONS = [
  { value: 'GOOGLE', label: 'Google Meet + Google Calendar' },
  { value: 'MICROSOFT', label: 'Microsoft Teams + Outlook Calendar' },
]

const LEAVE_TYPE_OPTIONS = [
  { value: 'FULL_DAY', label: 'Full Day' },
  { value: 'HALF_DAY', label: 'Half Day' },
]

function toDateInput(value) {
  const date = value ? new Date(value) : new Date()
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
  return local.toISOString().slice(0, 10)
}

function combineToIso(date, time) {
  if (!date || !time) return null
  return new Date(`${date}T${time}`).toISOString()
}

function toLocalDateTimeParts(value) {
  if (!value) {
    const now = new Date()
    const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
    const iso = local.toISOString()
    return { date: iso.slice(0, 10), time: iso.slice(11, 16) }
  }
  const date = new Date(value)
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
  const iso = local.toISOString()
  return { date: iso.slice(0, 10), time: iso.slice(11, 16) }
}

function splitByComma(text) {
  return (text || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
}

function getStoredCurrentUser() {
  try {
    const parsed = JSON.parse(localStorage.getItem('user') || '{}')
    const candidateId = Number(parsed?.id ?? parsed?.user_id ?? parsed?.userId)
    return {
      id: Number.isFinite(candidateId) && candidateId > 0 ? candidateId : null,
      name: String(parsed?.name || parsed?.userName || '').trim(),
    }
  } catch {
    return { id: null, name: '' }
  }
}

function SectionLabel({ children, required = false }) {
  return (
    <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.14em] text-on-surface-variant">
      {children}
      {required ? <span className="ml-1 text-[#dc2626]">*</span> : null}
    </p>
  )
}

export default function BridgeActionCenterModal({
  isOpen,
  onClose,
  defaultDate,
  defaultStartTime = '10:00',
  defaultEndTime = '11:00',
  defaultTeamId = '',
  mode = 'create',
  editEvent = null,
  onCreated,
}) {
  const [actionType, setActionType] = useState('meeting')
  const [message, setMessage] = useState('')

  const [meeting, setMeeting] = useState({
    title: '',
    provider: 'GOOGLE',
    date: toDateInput(defaultDate),
    startTime: defaultStartTime,
    endTime: defaultEndTime,
    description: '',
    teamId: defaultTeamId,
    attendeesText: '',
    addGoogleMeet: true,
    syncToGoogle: false,
    shareOnProjectChannel: false,
    reminderMinutes: 10,
  })

  const currentUser = getStoredCurrentUser()
  const [leaveEntry, setLeaveEntry] = useState({
    userId: currentUser.id ? String(currentUser.id) : '',
    leaveDate: toDateInput(defaultDate),
    leaveType: 'FULL_DAY',
    remark: '',
  })

  const { data: teamOptions = [] } = useGetTeamOptionsQuery({ page: 1, page_size: 100 }, { skip: !isOpen })
  const { data: googleStatus } = useGetGoogleCalendarStatusQuery(undefined, { skip: !isOpen })
  const { data: microsoftStatus } = useGetMicrosoftCalendarStatusQuery(undefined, { skip: !isOpen })

  const [createLeave, createLeaveState] = useCreateLeaveMutation()
  const [loadGoogleConnectUrl, connectState] = useGetGoogleCalendarConnectUrlMutation()
  const [loadMicrosoftConnectUrl, microsoftConnectState] = useGetMicrosoftCalendarConnectUrlMutation()
  const [disconnectGoogleCalendar, disconnectState] = useDisconnectGoogleCalendarMutation()
  const [disconnectMicrosoftCalendar, disconnectMicrosoftState] = useDisconnectMicrosoftCalendarMutation()
  const [createMeeting, createMeetingState] = useCreateCalendarMeetingMutation()
  const [updateCalendarEvent, updateCalendarEventState] = useUpdateCalendarEventMutation()

  const isEditMode = mode === 'edit' && Boolean(editEvent?.id)

  useEffect(() => {
    if (!isOpen) return

    if (isEditMode && editEvent) {
      const startParts = toLocalDateTimeParts(editEvent.startTime)
      const endParts = toLocalDateTimeParts(editEvent.endTime)
      const attendees = Array.isArray(editEvent.attendees) ? editEvent.attendees : []
      const attendeeEmails = attendees
        .map((row) => String(row?.email || '').trim())
        .filter(Boolean)
        .join(', ')

      setActionType('meeting')
      setMeeting((prev) => ({
        ...prev,
        title: String(editEvent.title || ''),
        provider: String(editEvent.meetingProvider || 'GOOGLE').toUpperCase() === 'MICROSOFT' ? 'MICROSOFT' : 'GOOGLE',
        date: startParts.date,
        startTime: startParts.time,
        endTime: endParts.time,
        description: String(editEvent.description || ''),
        teamId: editEvent.teamId ? String(editEvent.teamId) : '',
        attendeesText: attendeeEmails,
        syncToGoogle: Boolean(editEvent.syncToGoogle),
        addGoogleMeet: Boolean(editEvent.meetLink),
        shareOnProjectChannel: false,
        reminderMinutes: Number(editEvent.reminderMinutes || 10),
      }))
      setMessage('')
      return
    }

    const dateValue = toDateInput(defaultDate)
    setMeeting((prev) => ({
      ...prev,
      date: dateValue,
      startTime: defaultStartTime,
      endTime: defaultEndTime,
      teamId: defaultTeamId || prev.teamId || '',
    }))
    setLeaveEntry((prev) => ({
      ...prev,
      userId: currentUser.id ? String(currentUser.id) : prev.userId || '',
      leaveDate: dateValue,
    }))
    setMessage('')
  }, [currentUser.id, defaultDate, defaultEndTime, defaultStartTime, defaultTeamId, isOpen, isEditMode, editEvent])

  useEffect(() => {
    if (!isOpen) return undefined
    const prevBodyOverflow = document.body.style.overflow
    const prevHtmlOverflow = document.documentElement.style.overflow
    document.body.style.overflow = 'hidden'
    document.documentElement.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prevBodyOverflow
      document.documentElement.style.overflow = prevHtmlOverflow
    }
  }, [isOpen])

  if (!isOpen) return null

  const hasGoogleConnected = Boolean(googleStatus?.connected)
  const hasMicrosoftConnected = Boolean(microsoftStatus?.connected)
  const selectedProvider = String(meeting.provider || 'GOOGLE').toUpperCase() === 'MICROSOFT' ? 'MICROSOFT' : 'GOOGLE'
  const canSyncSelectedProvider = selectedProvider === 'MICROSOFT' ? hasMicrosoftConnected : hasGoogleConnected
  const providerLabel = selectedProvider === 'MICROSOFT' ? 'Microsoft Outlook Calendar' : 'Google Calendar'
  const providerMeetingLabel = selectedProvider === 'MICROSOFT' ? 'Microsoft Teams' : 'Google Meet'
  const isSubmitting =
    createMeetingState.isLoading ||
    createLeaveState.isLoading ||
    updateCalendarEventState.isLoading

  async function handleConnectGoogle() {
    setMessage('')
    try {
      const response = await loadGoogleConnectUrl().unwrap()
      if (response?.url) {
        window.open(response.url, '_blank', 'noopener,noreferrer')
        return
      }
      setMessage('Google connect URL is unavailable.')
    } catch (error) {
      const detail = error?.data?.detail || error?.error || 'Unable to start Google Calendar connection.'
      setMessage(String(detail))
    }
  }

  async function handleDisconnectGoogle() {
    setMessage('')
    try {
      await disconnectGoogleCalendar().unwrap()
      setMeeting((prev) => ({ ...prev, syncToGoogle: false }))
      setMessage('Google Calendar disconnected. You can connect again.')
    } catch (error) {
      const detail = error?.data?.detail || error?.error || 'Unable to disconnect Google Calendar.'
      setMessage(String(detail))
    }
  }

  async function handleConnectMicrosoft() {
    setMessage('')
    try {
      const response = await loadMicrosoftConnectUrl().unwrap()
      if (response?.url) {
        window.open(response.url, '_blank', 'noopener,noreferrer')
        return
      }
      setMessage('Microsoft connect URL is unavailable.')
    } catch (error) {
      const detail = error?.data?.detail || error?.error || 'Unable to start Microsoft Calendar connection.'
      setMessage(String(detail))
    }
  }

  async function handleDisconnectMicrosoft() {
    setMessage('')
    try {
      await disconnectMicrosoftCalendar().unwrap()
      setMeeting((prev) => ({ ...prev, syncToGoogle: false }))
      setMessage('Microsoft Calendar disconnected. You can connect again.')
    } catch (error) {
      const detail = error?.data?.detail || error?.error || 'Unable to disconnect Microsoft Calendar.'
      setMessage(String(detail))
    }
  }

  async function handleSubmit() {
    setMessage('')

    try {
      if (actionType === 'meeting') {
        const startTime = combineToIso(meeting.date, meeting.startTime)
        const endTime = combineToIso(meeting.date, meeting.endTime)
        if (!meeting.title.trim() || !startTime || !endTime) {
          setMessage('Meeting title, date, and time are required.')
          return
        }
        if (meeting.addGoogleMeet && !meeting.syncToGoogle) {
          setMessage(`Sync to ${providerLabel} required for ${providerMeetingLabel} link generation.`)
          return
        }
        if (meeting.shareOnProjectChannel && !meeting.syncToGoogle) {
          setMessage(`Sync to ${providerLabel} required to share on project channel.`)
          return
        }
        if (meeting.syncToGoogle && !canSyncSelectedProvider) {
          setMessage(`${providerLabel} is not connected. Connect it from Integrations.`)
          return
        }

        const attendees = splitByComma(meeting.attendeesText).map((email) => ({ email }))

        if (isEditMode && editEvent?.id) {
          await updateCalendarEvent({
            eventId: editEvent.id,
            eventType: 'meeting',
            title: meeting.title.trim(),
            startTime,
            endTime,
            description: meeting.description.trim(),
            visibility: editEvent.visibility || 'PRIVATE',
            reminderMinutes: Number(meeting.reminderMinutes) || 0,
          }).unwrap()
        } else {
          await createMeeting({
            title: meeting.title.trim(),
            startTime,
            endTime,
            description: meeting.description.trim(),
            provider: selectedProvider,
            projectId: null,
            teamId: Number(meeting.teamId) || null,
            attendees,
            notifyAll: true,
            syncToGoogle: Boolean(meeting.syncToGoogle),
            addGoogleMeet: Boolean(meeting.addGoogleMeet),
            shareOnProjectChannel: Boolean(meeting.shareOnProjectChannel),
            reminderMinutes: meeting.syncToGoogle ? Number(meeting.reminderMinutes) || 0 : null,
          }).unwrap()
        }

        appToast.success(isEditMode ? 'Meeting updated successfully.' : 'Meeting scheduled successfully.')
        onCreated?.()
        onClose()
        return
      }

      if (isEditMode) {
        setMessage('Leave/holiday overlays are read-only.')
        return
      }

      if (!leaveEntry.leaveDate) {
        setMessage('Leave date is required.')
        return
      }
      const requestUserId = Number(leaveEntry.userId || currentUser.id || 0)
      if (!requestUserId) {
        setMessage('Unable to determine your account. Please refresh and try again.')
        return
      }

      await createLeave({
        userId: requestUserId,
        leaveDate: leaveEntry.leaveDate,
        leaveType: leaveEntry.leaveType,
        remark: leaveEntry.remark?.trim() || null,
      }).unwrap()

      appToast.success('Leave requested successfully. Sent for manager approval.')
      onCreated?.()
      onClose()
      return
    } catch (error) {
      const apiMessage = error?.data?.detail?.message || error?.data?.detail || error?.error
      setMessage(String(apiMessage || 'Unable to save action.'))
    }
  }

  const titleValue = actionType === 'meeting' ? meeting.title : 'Mark Leave'

  return (
    <div className="fixed inset-0 z-[140] flex items-center justify-center bg-black/35 p-4 backdrop-blur-[8px]">
      <div className="flex max-h-[92vh] w-full max-w-5xl overflow-hidden rounded-3xl bg-surface shadow-2xl">
        <aside className="app-hide-scrollbar flex w-full flex-col gap-6 overflow-y-auto bg-surface-container-low p-7 md:w-[320px]">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-gradient-to-br from-primary to-primary-container text-white">
              <span className="material-symbols-outlined text-base">add</span>
            </div>
            <p className="text-lg font-bold">{isEditMode ? 'Edit Action' : 'New Action'}</p>
          </div>

          <div>
            <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.14em] text-on-surface-variant">Action Type</p>
            <div className="space-y-2">
              {ACTION_TYPES.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => {
                    if (isEditMode) return
                    setActionType(item.id)
                  }}
                  disabled={isEditMode && item.id !== actionType}
                  className={`flex w-full items-center gap-3 rounded-md px-4 py-3 text-left text-sm font-medium ${
                    actionType === item.id
                      ? 'bg-white text-primary shadow'
                      : 'text-on-surface-variant hover:bg-surface-container-high'
                  } ${isEditMode && item.id !== actionType ? 'cursor-not-allowed opacity-50' : ''}`}
                >
                  <span className="material-symbols-outlined text-base">{item.icon}</span>
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-auto">
            <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.14em] text-on-surface-variant">Integrations</p>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleConnectGoogle}
                  disabled={connectState.isLoading || hasGoogleConnected}
                  className="flex h-10 min-w-10 items-center justify-center rounded-full border border-outline-variant/20 bg-white px-3 text-xs font-semibold text-primary shadow-sm disabled:opacity-50"
                  title={hasGoogleConnected ? 'Google Calendar connected' : 'Connect Google Calendar'}
                >
                  G
                </button>
                {hasGoogleConnected ? (
                  <button
                    type="button"
                    onClick={handleDisconnectGoogle}
                    disabled={disconnectState.isLoading}
                    className="rounded-full border border-outline-variant/20 bg-white px-3 py-2 text-xs font-semibold text-on-surface shadow-sm disabled:opacity-50"
                    title="Disconnect Google Calendar"
                  >
                    Disconnect
                  </button>
                ) : null}
                <span className="text-xs text-on-surface-variant">
                  {hasGoogleConnected ? 'Google connected' : 'Google not connected'}
                </span>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleConnectMicrosoft}
                  disabled={microsoftConnectState.isLoading || hasMicrosoftConnected}
                  className="flex h-10 min-w-10 items-center justify-center rounded-full border border-outline-variant/20 bg-white px-3 text-xs font-semibold text-primary shadow-sm disabled:opacity-50"
                  title={hasMicrosoftConnected ? 'Microsoft Calendar connected' : 'Connect Microsoft Calendar'}
                >
                  M
                </button>
                {hasMicrosoftConnected ? (
                  <button
                    type="button"
                    onClick={handleDisconnectMicrosoft}
                    disabled={disconnectMicrosoftState.isLoading}
                    className="rounded-full border border-outline-variant/20 bg-white px-3 py-2 text-xs font-semibold text-on-surface shadow-sm disabled:opacity-50"
                    title="Disconnect Microsoft Calendar"
                  >
                    Disconnect
                  </button>
                ) : null}
                <span className="text-xs text-on-surface-variant">
                  {hasMicrosoftConnected ? 'Microsoft connected' : 'Microsoft not connected'}
                </span>
              </div>
            </div>
          </div>
        </aside>

        <section className="relative flex min-h-[560px] flex-1 flex-col overflow-hidden bg-surface-container-lowest p-7">
          <button
            type="button"
            onClick={onClose}
            className="absolute right-6 top-6 rounded-full p-1 text-on-surface-variant hover:bg-surface-container-low"
          >
            <span className="material-symbols-outlined">close</span>
          </button>

          <div className="app-hide-scrollbar space-y-5 overflow-y-auto pr-1">
            {actionType === 'meeting' ? (
              <div>
                <SectionLabel required>Event Title</SectionLabel>
                <input
                  value={meeting.title}
                  onChange={(event) => setMeeting((prev) => ({ ...prev, title: event.target.value }))}
                  placeholder="Add meeting title"
                  className="w-full rounded-md border-none bg-surface-container-low px-4 py-3 text-sm font-medium"
                />
              </div>
            ) : null}

            {actionType === 'meeting' ? (
              <>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <SectionLabel required>Date</SectionLabel>
                    <AppDateInput
                      value={meeting.date}
                      onChange={(value) => setMeeting((prev) => ({ ...prev, date: value }))}
                      inputClassName="w-full rounded-md border-none bg-surface-container-low px-4 py-3 text-sm font-medium shadow-none"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <SectionLabel required>Start</SectionLabel>
                      <AppTimeInput
                        value={meeting.startTime}
                        onChange={(val) => setMeeting((prev) => ({ ...prev, startTime: val }))}
                        inputClassName="w-full rounded-md border-none bg-surface-container-low px-4 py-3 text-sm font-bold shadow-none"
                      />
                    </div>
                    <div>
                      <SectionLabel required>End</SectionLabel>
                      <AppTimeInput
                        value={meeting.endTime}
                        onChange={(val) => setMeeting((prev) => ({ ...prev, endTime: val }))}
                        inputClassName="w-full rounded-md border-none bg-surface-container-low px-4 py-3 text-sm font-bold shadow-none"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <SectionLabel>Team</SectionLabel>
                  <SearchableCombobox
                    value={meeting.teamId}
                    onChange={(nextTeamId) => setMeeting((prev) => ({ ...prev, teamId: nextTeamId }))}
                    placeholder="Select team"
                    searchPlaceholder="Search teams..."
                    emptyText="No teams found."
                    items={teamOptions.map((team) => ({ value: String(team.id), label: team.name }))}
                  />
                </div>

                <div>
                  <SectionLabel>Participants (Additional Guests)</SectionLabel>
                  <input
                    value={meeting.attendeesText}
                    onChange={(event) => setMeeting((prev) => ({ ...prev, attendeesText: event.target.value }))}
                    placeholder="alex@company.com, client@example.com"
                    className="w-full rounded-md border-none bg-surface-container-low px-4 py-3 text-sm font-medium"
                  />
                  <p className="mt-1 text-xs text-on-surface-variant">Selected team members are included automatically.</p>
                </div>

                <div>
                  <SectionLabel>Agenda</SectionLabel>
                  <textarea
                    rows={3}
                    value={meeting.description}
                    onChange={(event) => setMeeting((prev) => ({ ...prev, description: event.target.value }))}
                    placeholder="Define the next steps and deliverables..."
                    className="w-full resize-none rounded-md border-none bg-surface-container-low px-4 py-3 text-sm"
                  />
                </div>

                <div>
                  <SectionLabel>Meeting Provider</SectionLabel>
                  <AppDropdown
                    value={meeting.provider}
                    disabled={isEditMode}
                    options={MEETING_PROVIDER_OPTIONS}
                    triggerClassName="rounded-md border-none bg-surface-container-low px-4 py-3 shadow-none"
                    onChange={(value) =>
                      setMeeting((prev) => ({
                        ...prev,
                        provider: value === 'MICROSOFT' ? 'MICROSOFT' : 'GOOGLE',
                        syncToGoogle: false,
                      }))
                    }
                  />
                </div>

                <div>
                  <SectionLabel>{providerMeetingLabel}</SectionLabel>
                  <div className="flex items-center justify-between rounded-md border border-outline-variant/30 bg-surface-container-low px-3 py-2.5">
                    <div className="flex items-center gap-3">
                      <span className="material-symbols-outlined text-[#1a73e8]">video_call</span>
                      <div>
                        <p className="text-sm font-medium text-on-surface">
                          Add {providerMeetingLabel} video conferencing
                        </p>
                        <p className="text-xs text-on-surface-variant">Requires {providerLabel} sync</p>
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={meeting.addGoogleMeet}
                      disabled={isEditMode}
                      onChange={(event) => setMeeting((prev) => ({ ...prev, addGoogleMeet: event.target.checked }))}
                      className="h-4 w-4"
                    />
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between rounded-md border border-outline-variant/30 bg-surface-container-low px-3 py-2.5">
                    <span className="text-sm font-medium text-on-surface">
                      Sync to {providerLabel}
                      {meeting.addGoogleMeet ? <span className="ml-1 text-[#dc2626]">*</span> : null}
                    </span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={meeting.syncToGoogle}
                      disabled={!canSyncSelectedProvider || isEditMode}
                      onClick={() => setMeeting((prev) => ({ ...prev, syncToGoogle: !prev.syncToGoogle }))}
                      className={`relative h-6 w-11 rounded-full transition-colors ${
                        meeting.syncToGoogle ? 'bg-primary' : 'bg-outline-variant'
                      } disabled:opacity-50`}
                    >
                      <span
                        className={`absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${
                          meeting.syncToGoogle ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                  {meeting.syncToGoogle ? (
                    <div>
                      <SectionLabel>Reminder (Minutes Before Meeting)</SectionLabel>
                      <input
                        type="number"
                        min={0}
                        value={meeting.reminderMinutes}
                        onChange={(event) => setMeeting((prev) => ({ ...prev, reminderMinutes: event.target.value }))}
                        className="w-full rounded-md border-none bg-surface-container-low px-4 py-3 text-sm font-medium"
                      />
                    </div>
                  ) : null}
                  {!canSyncSelectedProvider ? (
                    <p className="text-xs text-amber-700">
                      {providerLabel} account needs to be connected for sync, reminders, and {providerMeetingLabel}{' '}
                      link generation.
                    </p>
                  ) : (
                    <p className="text-xs text-on-surface-variant">
                      With sync enabled, {providerLabel} reminders will apply for this meeting.
                    </p>
                  )}
                  <p className="text-xs text-on-surface-variant">
                    Notify team members sends invite notifications to selected team members and additional participants.
                  </p>
                </div>

                <div>
                  <SectionLabel>Share on Project Channel</SectionLabel>
                  <div className="flex items-center justify-between rounded-md border border-outline-variant/30 bg-surface-container-low px-3 py-2.5">
                    <div>
                      <p className="text-sm font-medium text-on-surface">Share on Project Channel</p>
                      <p className="text-xs text-on-surface-variant">
                        Posts title, agenda, timing and meeting link in the team project channel.
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      checked={meeting.shareOnProjectChannel}
                      disabled={isEditMode}
                      onChange={(event) =>
                        setMeeting((prev) => ({ ...prev, shareOnProjectChannel: event.target.checked }))
                      }
                      className="h-4 w-4"
                    />
                  </div>
                </div>
              </>
            ) : null}



            {actionType === 'leave' ? (
              <>
                <div>
                  <SectionLabel required>Your Account</SectionLabel>
                  <input
                    value={currentUser.name || 'Signed-in user'}
                    readOnly
                    className="w-full rounded-md border-none bg-surface-container-low px-4 py-3 text-sm font-medium text-on-surface"
                  />
                  <p className="mt-1 text-xs text-on-surface-variant">
                    Leave requests are submitted for your own account and routed for manager approval.
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <SectionLabel required>Leave Date</SectionLabel>
                    <AppDateInput
                      value={leaveEntry.leaveDate}
                      onChange={(value) => setLeaveEntry((prev) => ({ ...prev, leaveDate: value }))}
                      inputClassName="w-full rounded-md border-none bg-surface-container-low px-4 py-3 text-sm font-medium shadow-none"
                    />
                  </div>
                  <div>
                    <SectionLabel>Leave Type</SectionLabel>
                    <AppDropdown
                      value={leaveEntry.leaveType}
                      options={LEAVE_TYPE_OPTIONS}
                      triggerClassName="rounded-md border-none bg-surface-container-low px-4 py-3 shadow-none"
                      onChange={(value) => setLeaveEntry((prev) => ({ ...prev, leaveType: value }))}
                    />
                  </div>
                </div>

                <div>
                  <SectionLabel>Remark</SectionLabel>
                  <textarea
                    rows={4}
                    value={leaveEntry.remark}
                    onChange={(event) => setLeaveEntry((prev) => ({ ...prev, remark: event.target.value }))}
                    placeholder="Optional note for leave marking..."
                    className="w-full resize-none rounded-md border-none bg-surface-container-low px-4 py-3 text-sm"
                  />
                </div>
              </>
            ) : null}
          </div>

          <div className="mt-auto border-t border-outline-variant/20 pt-5">
            {message ? <p className="mb-3 text-sm text-primary">{message}</p> : null}
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2.5 text-sm font-bold text-secondary transition-colors hover:text-primary"
              >
                Discard
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="rounded-md bg-gradient-to-br from-primary to-primary-container px-8 py-2.5 text-sm font-bold text-white shadow disabled:opacity-60"
              >
                {isSubmitting ? 'Saving...' : isEditMode ? 'Save Changes' : 'Create Action'}
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}
